/* eslint-disable prettier/prettier */
import * as React from "react";
import { Modal, Grid, TextField, Link, Snackbar, inputAdornmentClasses } from "@mui/material";
import { Alert } from "@mui/material";

// importing app setting
import { AuthenticationContext } from "../../../Context";
import { getHeaders } from "../../../utils/HeaderHelper";
import GeneralModal from "../../../modals/GeneralModal";
import { fetchDiagnosisOptions } from "../../../utils/DiagnosisSearch";
import { Autocomplete} from "@mui/material";
import type { DiagnosisOption } from "../../../utils/DiagnosisSearch";
import { useEffect, useState } from "react";
import { useFlash } from "../../../FlashContext";

// import helpers
import { normalizeWhitespaceForSave } from "../../../utils/WhitespaceHelper";

interface Props {
  patient_id: number;
  edit_medication: any;
  open: boolean;
  setOpen: any;
}

// constants
const DOSAGE_MAX_LENGTH = 50;
const INDICATION_MAX_LENGTH = 200;

const AddMedication: React.FC<Props> = (props: any) => {
  const authenticationSetting = React.useContext(AuthenticationContext);
  const { showToast } = useFlash();

  // other states
  const [error, setError] = React.useState<string>("");
  const [disabledButton, setDisabledButton] = React.useState(false);
  const [name, setName] = React.useState<string>(
    props.edit_medication != null ? props.edit_medication.name : ""
  );
  const [value, setValue] = React.useState<string>(
    props.edit_medication != null ? props.edit_medication.value : ""
  );
  const [medicationSearch, setMedicationSearch] = React.useState<any>(null);
  const [indication, setIndication] = React.useState(
    props.edit_medication != null ? props.edit_medication.indication : ""
  );
  const [diagnosisCode, setDiagnosisCode] = React.useState(
    props.edit_medication != null ? props.edit_medication.diagnosis_code : ""
  );
  const [diagnoses, setDiagnoses] = React.useState<DiagnosisOption[]>([]);
  const [selectedDiagnosis, setSelectedDiagnosis] =
    useState<DiagnosisOption | null>(null);

  const getNewOptions = async (newInput: string) => {
    if (!newInput) {
      setSelectedDiagnosis(null);
      setDiagnoses([]);
      return;
    }

    try {
      const options = await fetchDiagnosisOptions(
        newInput,
        authenticationSetting.csrfToken
      );
      setDiagnoses(options);
    } catch (error) {
      console.error("Failed to fetch diagnosis options:", error);
    }
  };

  React.useEffect(() => {
    if (!props.editData?.diagnosis_code) return;

    const code = props.editData.diagnosis_code;
    const description = props.editData.diagnosis_description ?? "";

    const hydratedOption: DiagnosisOption = [code, description];

    setSelectedDiagnosis(hydratedOption);
    setDiagnosisCode(code);
    setDiagnoses([hydratedOption]); // important!
  }, [props.editData]);

  React.useEffect(() => {
    if (name) {
      fetch(`/medication_search`, {
        method: "POST",
        headers: getHeaders(authenticationSetting.csrfToken),
        body: JSON.stringify({
          medication: {
            name: name,
          },
        }),
      })
        .then((result) => result.json())
        .then((result) => {
          if (typeof result.error !== "undefined") {
            setError(result.error);
            setDisabledButton(false);
            setMedicationSearch(null);
          } else {
            let medicationSearchObject = result.data.results;
            console.log("medicationSearchObject", medicationSearchObject);
            setMedicationSearch(medicationSearchObject);
          }
        })
        .catch((error) => {
          setError(error);
          setDisabledButton(false);
          setMedicationSearch(null);
        });
    }
  }, [name]);

  const closeModal = () => {
    props.setOpen(false);
  };

  const validForm = () => {
    return !!(name && value);
  };

  const updatePatient = () => {
    setError("");
    setDisabledButton(true);

    if (!name?.trim()) {
      showToast({
        title: "Medication name required",
        text: "Please enter a medication name.",
        type: "error",
      });
      setDisabledButton(false);
      return;
    }
    if (!value?.trim()) {
      showToast({
        title: "Medication dosage required",
        text: "Please enter a dosage.",
        type: "error",
      });
      setDisabledButton(false);
      return;
    }

    const normalizedMedication = {
      patient_id: props.patient_id,
      name: normalizeWhitespaceForSave(name),
      value: normalizeWhitespaceForSave(value),
      indication: normalizeWhitespaceForSave(indication),
      diagnosis_code: normalizeWhitespaceForSave(diagnosisCode),
      id: props.edit_medication?.id ?? null,
    };

    fetch(
      props.edit_medication ? "/update_medication" : "/add_medication",
      {
        method: "POST",
        headers: getHeaders(authenticationSetting.csrfToken),
        body: JSON.stringify({ medication: normalizedMedication }),
      }
    )
      .then((result) => result.json())
      .then((result) => {
        if (result.error) {
          showToast({
            title: props.edit_medication ? "Unable to update medication" : "Unable to add medication",
            text: result.error,
            type: "error",
          });
          setError(result.error);
          setDisabledButton(false);
          return;
        }

        showToast({
          title: props.edit_medication ? "Medication updated" : "Medication added",
          text: props.edit_medication
            ? "The medication has been updated."
            : "The medication has been added.",
          type: "success",
        });
        props.setOpen(false);
      })
      .catch((err) => {
        showToast({
          title: props.edit_medication ? "Unable to update medication" : "Unable to add medication",
          text: err?.message || "Something went wrong. Please try again.",
          type: "error",
        });
        setError(err);
        setDisabledButton(false);
      });
  };

  return (
    <GeneralModal
      open={props.open}
      title={(props.edit_medication != null ? "Edit" : "Add") + " Medication"}
      successCallback={updatePatient}
      closeCallback={closeModal}
      containerClassName="add-medication-modal"
      confirmButtonText="Save"
    >
      

      <Grid style={{ paddingTop: 20 }}>
       <TextField
          id="medication_name"
          label="Medication Name*"
          value={name}
          className="textInput"
          required
          variant="filled"
          onChange={(
            event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
          ) => {
            const { value } = event.target;
            setName(value);
          }}
          InputLabelProps={{ required: false }}
        />

        {medicationSearch && medicationSearch.length > 0 ? (
          <>
            <strong style={{ fontFamily: "QuicksandMedium" }}>
              Available Medications{" "}
              <small>(Click medication below to populate field)</small>
            </strong>

            <div
              style={{
                border: "1px solid #929292",
                borderRadius: 6,
                maxHeight: 200,
                overflow: "auto",
                padding: 10,
              }}
            >
              {medicationSearch.map((medication, index) => (
                <div
                  key={index}
                  className={index % 2 === 0 ? "rowEven" : "row"}
                  style={{
                    cursor: "pointer",
                    padding: 6,
                    fontFamily: "QuicksandMedium",
                  }}
                  onClick={() => {
                    setName(medication);
                    setMedicationSearch(null);
                  }}
                >
                  {medication}
                </div>
              ))}
            </div>
          </>
        ) : (
          <strong style={{ fontFamily: "QuicksandMedium" }}>
            <small>Begin typing a name to search for existing medications.</small>
          </strong>
        )}

        <TextField
          id="medication_dosage"
          label="Medication Dosage*"
          value={value}
          className="textInput"
          required
          variant="filled"
          onChange={(
            event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
          ) => {
            const { value } = event.target;
            if (value.length > DOSAGE_MAX_LENGTH) return;
            setValue(value);
          }}
          inputProps={{ maxLength: DOSAGE_MAX_LENGTH }}
          InputLabelProps={{ required: false }}
        />

        <TextField
          id="indication"
          label="Medication Indication"
          value={indication}
          className="textInput"
          required
          variant="filled"
          multiline
          rows={2}
          onChange={(
            event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
          ) => {
            const { value } = event.target;
            if (value.length > INDICATION_MAX_LENGTH) return;
            setIndication(value);
          }}
          inputProps={{ maxLength: INDICATION_MAX_LENGTH }}
          InputLabelProps={{ required: false }}
        />

        <Autocomplete<DiagnosisOption, false, false, true>
          id="codeId"
          options={diagnoses}
          className="facility-field facility-text"
          value={selectedDiagnosis}
          inputValue={diagnosisCode}
          freeSolo
          getOptionLabel={(option) => {
            if (typeof option === "string") return option;
            return `${option[0]} - ${option[1]}`;
          }}
          onChange={(_, value) => {
            if (!value) {
              setSelectedDiagnosis(null);
              setDiagnosisCode(""); 
              return;
            }

            if (typeof value === "string") {
              setDiagnosisCode(value);
              setSelectedDiagnosis(null);
              return;
            }

            setSelectedDiagnosis(value);
            setDiagnosisCode(value[0]);
          }}

          onInputChange={(_, newInput, reason) => {
            if (reason === "clear") {
              setSelectedDiagnosis(null);
              setDiagnosisCode(""); 
              setDiagnoses([]);     
              return;
            }

            if (reason !== "input") return;

            setDiagnosisCode(newInput);
            getNewOptions(newInput);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Diagnosis Code*"
              placeholder="Select Code"
              variant="outlined"
              InputLabelProps={{
                shrink: true,
                sx: {
                  transform: "translate(16px, 8px) scale(1)",
                  transformOrigin: "top left",
                  color: "#4A4442",
                  fontSize: 12,
                  fontWeight: 500,
                },
              }}
              InputProps={{
                ...params.InputProps,
                sx: {
                  paddingTop: "16px !important",
                  paddingLeft: "5px !important",
                  color: "#1E1E1E",
                  fontSize: 16,
                  fontWeight: 500,
                },
              }}
              sx={{
                height: 56,
                "& .MuiInputBase-input::placeholder": {
                  color: "#1E1E1E",
                  fontSize: 16,
                  fontWeight: 500,
                  opacity: 1,
                  lineHeight: 22,
                },
              }}
            />
          )}
        />
      </Grid>
    </GeneralModal>
  );
};

export default AddMedication;
